import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { HostingerPartnerBadge } from './HostingerPartnerBadge';

const heroLadyHoodieImg = '/src/assets/images/xweba_hero_lady_laptop_hoodie_1793401607879.jpg';

interface HeroProps {
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onNavigateToPackages?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onOpenAudit, onNavigateToPackages }) => {
  const [imgFailed, setImgFailed] = useState(false);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, callback?: () => void) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (callback) {
      e.preventDefault();
      callback();
    }
  };

  return (
    <section className="relative pt-24 pb-14 sm:pt-28 sm:pb-16 lg:pt-36 lg:pb-24 overflow-hidden bg-gradient-to-b from-white via-white to-slate-50/70 lg:bg-white border-b border-slate-200/70">
      {/* Desktop Full-Bleed Background Image of Lady with Laptop Wearing Xweba Branded Hoodie */}
      <div className="hidden lg:block absolute inset-0 pointer-events-none select-none overflow-hidden">
        {!imgFailed ? (
          <img
            src={heroLadyHoodieImg}
            alt="Xweba digital engineer wearing a Xweba branded hoodie working on a laptop"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover object-right"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-50 via-white to-orange-50/40" />
        )}

        {/* Smooth directional scrim on left side to keep hero copy crisp while showcasing the image on the right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 39%, rgba(255,255,255,0.95) 49%, rgba(255,255,255,0.32) 62%, rgba(255,255,255,0.0) 76%)',
          }}
        />
        {/* Top and bottom subtle feathering for header & section transition */}
        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-white/80 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white/50 to-transparent" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* Left Column: Core Positioning & Promise */}
          <div className="lg:col-span-7 space-y-5 sm:space-y-6">
            {/* Editorial Label */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs font-bold tracking-wider uppercase text-slate-500">
              <span className="text-[#F26522] font-black">AI DIGITAL GROWTH AGENCY</span>
              <span aria-hidden="true" className="text-slate-300">
                ·
              </span>
              <span>20+ YEARS HANDS-ON BRANDING &amp; DESIGN</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#0B2D52] tracking-tight leading-[1.12] [text-wrap:balance]">
              Build websites that are designed for{' '}
              <span className="relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-[#0B2D52] via-[#F26522] to-[#FF7A00]">
                both humans and AI.
              </span>
            </h1>

            {/* Core Sales Proposition */}
            <p className="text-base sm:text-xl text-slate-700 leading-relaxed font-normal max-w-2xl">
              We design <strong className="font-semibold text-[#0B2D52]">Pro WordPress Websites that are cited by AI</strong> and custom AI-ready platforms—backed by a{' '}
              <strong className="font-semibold text-[#0B2D52]">Branding Consultant with over 20 years of hands-on work and design</strong>,{' '}
              <strong className="font-semibold text-[#0B2D52]">Social Media Consulting</strong>, and{' '}
              <strong className="font-semibold text-[#0B2D52]">Digital / Smart Advertisement</strong>.
            </p>

            {/* Mobile & Tablet Organised Hero Visual Card (Cropped cleanly to the subject without the left studio wall) */}
            {!imgFailed && (
              <div className="lg:hidden pt-1">
                <div className="relative rounded-2xl overflow-hidden border border-slate-200/90 shadow-md bg-slate-900 aspect-4/3 sm:aspect-16/10">
                  <img
                    src={heroLadyHoodieImg}
                    alt="Xweba engineer wearing a Xweba branded hoodie working on a laptop"
                    referrerPolicy="no-referrer"
                    onError={() => setImgFailed(true)}
                    className="w-[162%] max-w-none h-full -ml-[62%] object-cover object-center"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B2D52]/90 via-[#0B2D52]/45 to-transparent px-4 py-3 flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-white tracking-tight">
                      Engineered for Human Conversion &amp; AI Search Visibility
                    </span>
                    <span className="text-[11px] font-bold text-[#F26522] bg-white/95 px-2.5 py-0.5 rounded-md shrink-0">
                      100/100 Standard
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* CTAs & Secondary Info */}
            <div className="pt-1 sm:pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href="/xwebaudit"
                onClick={(e) => handleLinkClick(e, onOpenAudit)}
                className="px-6 py-3.5 min-h-[48px] text-sm font-bold text-white bg-[#F26522] hover:bg-[#ea580c] active:scale-98 rounded-xl shadow-md shadow-orange-500/20 transition-all flex items-center justify-center gap-2 group cursor-pointer whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4 transition-transform group-hover:scale-110" />
                <span>Run Free Xwebaudit Diagnostic</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>

              <a
                href="/packages"
                onClick={(e) => handleLinkClick(e, onNavigateToPackages)}
                className="px-6 py-3.5 min-h-[48px] text-sm font-semibold text-[#0B2D52] bg-white hover:bg-slate-50 rounded-xl border border-slate-300 shadow-2xs transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <span>Explore AI Packages &amp; WaaS</span>
              </a>
            </div>

            {/* The 3-Step Promise Ribbon */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-1 pb-1">
              <div className="p-3.5 rounded-xl bg-white/95 backdrop-blur-xs border border-slate-200/90 shadow-2xs hover:border-[#F26522]/40 transition-colors">
                <div className="text-xs font-bold text-[#F26522] mb-1">01. PROMISE</div>
                <div className="font-bold text-slate-900 text-sm sm:text-base">Get Found.</div>
                <div className="text-xs text-slate-600 mt-0.5 leading-snug">
                  Pro WordPress &amp; AI websites cited by ChatGPT &amp; Google.
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-white/95 backdrop-blur-xs border border-slate-200/90 shadow-2xs hover:border-[#F26522]/40 transition-colors">
                <div className="text-xs font-bold text-[#0B2D52] mb-1">02. PROMISE</div>
                <div className="font-bold text-slate-900 text-sm sm:text-base">Get Understood.</div>
                <div className="text-xs text-slate-600 mt-0.5 leading-snug">
                  20+ yrs hands-on brand design &amp; entity graphs.
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-white/95 backdrop-blur-xs border border-slate-200/90 shadow-2xs hover:border-[#F26522]/40 transition-colors">
                <div className="text-xs font-bold text-emerald-600 mb-1">03. PROMISE</div>
                <div className="font-bold text-slate-900 text-sm sm:text-base">Get Chosen.</div>
                <div className="text-xs text-slate-600 mt-0.5 leading-snug">
                  Smart ads, social consulting &amp; 24/7 AI CRM sync.
                </div>
              </div>
            </div>

            {/* Quiet reassurance text */}
            <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-xs text-slate-600 pt-1">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>20+ Yrs Branding Consultant</span>
              </div>
              <span aria-hidden="true" className="hidden sm:inline">
                ·
              </span>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>Pro WordPress Cited by AI</span>
              </div>
              <span aria-hidden="true" className="hidden sm:inline">
                ·
              </span>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>Social Media &amp; Smart Ads</span>
              </div>
            </div>

            {/* Official Hostinger Infrastructure Partnership Badge */}
            <div className="pt-1">
              <HostingerPartnerBadge variant="card" />
            </div>
          </div>

          {/* Right Column: Clean open viewport revealing the background image of the lady in the Xweba hoodie with laptop */}
          <div className="hidden lg:block lg:col-span-5 min-h-[480px]" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
};
